'use client';

import { setCurrencyCookie } from '@/actions/villages';
import { create } from 'zustand';
import { persist, devtools } from 'zustand/middleware';
import { USD_MULTIPLIER, GBP_MULTIPLIER } from '@/utils/currency';

export type Currency = {
  label: string;
  value: string;
  symbol: '€' | '£' | '$';
};

type CurrencyStore = {
  currency: Currency;
  setCurrency: (currency: Currency) => void;
  applyCurrency: (value: number) => number;
  saveLocalStorageCurrency(currencyType: string): Promise<void>;
};

const defaultCurrency: Currency = {
  label: 'Euro',
  symbol: '€',
  value: 'EUR',
};

const defaultUsdCurrency: Currency = {
  label: 'Dollar',
  symbol: '$',
  value: 'USD',
};

const defaultPoundCurrency: Currency = {
  label: 'Pound',
  symbol: '£',
  value: 'GBP',
};

const useCurrency = create<CurrencyStore>()(
  devtools(
    persist(
      (set, get) => ({
        currency: defaultCurrency,
        setCurrency: (currency: Currency) => set({ currency }),
        applyCurrency: (value: number) => {
          const currency = get().currency.value;
          if (currency === 'USD') return Math.round(value * USD_MULTIPLIER);
          if (currency === 'GBP') return Math.round(value * GBP_MULTIPLIER);
          return Math.round(value);
        },
        saveLocalStorageCurrency: async (currencyType: string) => {
          let newCurrency = defaultCurrency;

          if (currencyType.toUpperCase() === 'GBP') {
            newCurrency = defaultPoundCurrency;
          } else if (currencyType.toUpperCase() === 'USD') {
            newCurrency = defaultUsdCurrency;
          }

          set({ currency: newCurrency });

          await setCurrencyCookie(currencyType.toLowerCase());
        },
      }),
      {
        name: 'currency-store',
      },
    ),
    { enabled: process.env.NODE_ENV === 'development' },
  ),
);

export default useCurrency;
